import { createAsyncThunk } from '@reduxjs/toolkit';
import api, { AxiosError } from 'api';
import type { TemplateFilters } from 'views/retainerAgreement/types';
import type {
    TemplateCreatePayload,
    TemplateUpdatePayload,
    TemplateAssociationPayload,
    TemplatePreviewPayload,
    VersionComparePayload,
    BulkOperationPayload,
    FolderCreatePayload,
    MoveToFolderPayload,
} from 'api/rest/retainer_templates';

// Template CRUD Operations
export const loadTemplates = createAsyncThunk(
    'api/retainer-templates(GET)',
    async (
        filters: (TemplateFilters & { page?: number; per_page?: number }) | undefined = undefined,
        thunkApi?
    ) => {
        const { rejectWithValue } = thunkApi || {};
        try {
            return await api.retainerTemplates.templates.get(filters);
        } catch (e) {
            return (
                rejectWithValue?.((e as AxiosError).response?.data) ??
                (e as AxiosError).response?.data
            );
        }
    }
);

export const loadTemplate = createAsyncThunk(
    'api/retainer-templates/:id(GET)',
    async (templateId: number, { rejectWithValue }) => {
        try {
            return await api.retainerTemplates.templates.getById(templateId);
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const createTemplate = createAsyncThunk(
    'api/retainer-templates(POST)',
    async (payload: TemplateCreatePayload, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.retainerTemplates.templates.post(payload);
            // Refresh templates list after creation
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const updateTemplate = createAsyncThunk(
    'api/retainer-templates/:id(PUT)',
    async (
        { id, updates }: { id: number; updates: TemplateUpdatePayload },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response = await api.retainerTemplates.templates.put(id, updates);
            // Refresh templates list after update
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const deleteTemplate = createAsyncThunk(
    'api/retainer-templates/:id(DELETE)',
    async (templateId: number, { dispatch, rejectWithValue }) => {
        try {
            await api.retainerTemplates.templates.delete(templateId);
            // Refresh templates list after deletion
            dispatch(loadTemplates(undefined));
            return templateId;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

// Template Operations
export const bulkDeleteTemplates = createAsyncThunk(
    'api/retainer-templates/bulk-delete(POST)',
    async (payload: BulkOperationPayload, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.retainerTemplates.templateOperations.bulkDelete(payload);
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const duplicateTemplate = createAsyncThunk(
    'api/retainer-templates/:id/duplicate(POST)',
    async (templateId: number, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.retainerTemplates.templateOperations.duplicate(templateId);
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const toggleFavorite = createAsyncThunk(
    'api/retainer-templates/:id/toggle-favorite(POST)',
    async (templateId: number, { dispatch, rejectWithValue }) => {
        try {
            const response =
                await api.retainerTemplates.templateOperations.toggleFavorite(templateId);
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const publishTemplate = createAsyncThunk(
    'api/retainer-templates/:id/publish(POST)',
    async (templateId: number, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.retainerTemplates.templateOperations.publish(templateId);
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const unpublishTemplate = createAsyncThunk(
    'api/retainer-templates/:id/unpublish(POST)',
    async (templateId: number, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.retainerTemplates.templateOperations.unpublish(templateId);
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const bulkPublishTemplates = createAsyncThunk(
    'api/retainer-templates/bulk-publish(POST)',
    async (payload: BulkOperationPayload, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.retainerTemplates.templateOperations.bulkPublish(payload);
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const bulkUnpublishTemplates = createAsyncThunk(
    'api/retainer-templates/bulk-unpublish(POST)',
    async (payload: BulkOperationPayload, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.retainerTemplates.templateOperations.bulkUnpublish(payload);
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const bulkDuplicateTemplates = createAsyncThunk(
    'api/retainer-templates/bulk-duplicate(POST)',
    async (payload: BulkOperationPayload, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.retainerTemplates.templateOperations.bulkDuplicate(payload);
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const markTemplateUsed = createAsyncThunk(
    'api/retainer-templates/:id/mark-used(POST)',
    async (templateId: number, { rejectWithValue }) => {
        try {
            return await api.retainerTemplates.templateOperations.markUsed(templateId);
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

// Version Management
export const loadTemplateVersions = createAsyncThunk(
    'api/retainer-templates/:id/versions(GET)',
    async (templateId: number, { rejectWithValue }) => {
        try {
            return await api.retainerTemplates.versions.get(templateId);
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const loadTemplateVersion = createAsyncThunk(
    'api/retainer-templates/:id/versions/:versionId(GET)',
    async (
        { templateId, versionId }: { templateId: number; versionId: number },
        { rejectWithValue }
    ) => {
        try {
            return await api.retainerTemplates.versions.getById(templateId, versionId);
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const compareVersions = createAsyncThunk(
    'api/retainer-templates/:id/versions/compare(POST)',
    async (
        { templateId, payload }: { templateId: number; payload: VersionComparePayload },
        { rejectWithValue }
    ) => {
        try {
            return await api.retainerTemplates.versions.compare(templateId, payload);
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

// Folder Management
export const loadFolders = createAsyncThunk(
    'api/retainer-templates/folders(GET)',
    async (_, { rejectWithValue }) => {
        try {
            return await api.retainerTemplates.folders.get();
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const createFolder = createAsyncThunk(
    'api/retainer-templates/folders(POST)',
    async (payload: FolderCreatePayload, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.retainerTemplates.folders.post(payload);
            dispatch(loadFolders());
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const deleteFolder = createAsyncThunk(
    'api/retainer-templates/folders/:id(DELETE)',
    async (folderId: number, { dispatch, rejectWithValue }) => {
        try {
            await api.retainerTemplates.folders.delete(folderId);
            dispatch(loadFolders());
            return folderId;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const moveTemplateToFolder = createAsyncThunk(
    'api/retainer-templates/:id/folder(PUT)',
    async (
        { templateId, payload }: { templateId: number; payload: MoveToFolderPayload },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response = await api.retainerTemplates.moveToFolder(templateId, payload);
            dispatch(loadTemplates(undefined));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

// Utility Endpoints
export const loadRecentlyUsedTemplates = createAsyncThunk(
    'api/retainer-templates/recently-used(GET)',
    async (limit?: number, thunkApi?) => {
        const { rejectWithValue } = thunkApi || {};
        try {
            return await api.retainerTemplates.utility.getRecentlyUsed(limit);
        } catch (e) {
            return (
                rejectWithValue?.((e as AxiosError).response?.data) ??
                (e as AxiosError).response?.data
            );
        }
    }
);

export const loadFavoriteTemplates = createAsyncThunk(
    'api/retainer-templates/favorites(GET)',
    async (_, { rejectWithValue }) => {
        try {
            return await api.retainerTemplates.utility.getFavorites();
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const loadTemplatesForSelection = createAsyncThunk(
    'api/retainer-templates/for-selection(GET)',
    async (
        filters?: { country?: string; program?: string; type?: string; limit?: number },
        thunkApi?
    ) => {
        const { rejectWithValue } = thunkApi || {};
        try {
            return await api.retainerTemplates.utility.getForSelection(filters);
        } catch (e) {
            return (
                rejectWithValue?.((e as AxiosError).response?.data) ??
                (e as AxiosError).response?.data
            );
        }
    }
);

// Application Template Integration
export const associateTemplateWithApplication = createAsyncThunk(
    'api/applications/:id/template(PUT)',
    async (
        { applicationId, payload }: { applicationId: number; payload: TemplateAssociationPayload },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response = await api.retainerTemplates.applicationTemplates.associate(
                applicationId,
                payload
            );
            // Reload associated template
            dispatch(loadApplicationTemplate(applicationId));
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

export const loadApplicationTemplate = createAsyncThunk(
    'api/applications/:id/template(GET)',
    async (applicationId: number, { rejectWithValue }) => {
        try {
            return await api.retainerTemplates.applicationTemplates.get(applicationId);
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

// Template Preview
export const previewTemplateGeneration = createAsyncThunk(
    'api/retainer-templates/:id/preview-generation(POST)',
    async (
        { templateId, payload }: { templateId: number; payload: TemplatePreviewPayload },
        { rejectWithValue }
    ) => {
        try {
            return await api.retainerTemplates.preview.generate(templateId, payload);
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);
